import {Injectable} from "@angular/core";
import {BehaviorSubject, firstValueFrom, Observable} from "rxjs";
import {Router} from "@angular/router";
import {environment} from "src/environments/environment";
import {HttpClient} from "@angular/common/http";
import {SessionStorageService} from "./session-storage.service";
import {WebAuthnService} from "./web-authn.service";
import {IndexedDbService} from "./indexed-db.service";
import {UserInfoService} from "./user-info.service";
import {map} from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AuthService {

  private readonly IS_LOGGED_OUT_KEY = "isLoggedOut";
  private readonly IS_NEW_KEY = "isNew";
  private readonly IS_ID_CARD_AUTH_KEY = "isIdCardAuthenticate";
  private readonly GLOBAL_PATHS_TO_CHECK = ["price-list", "terms", "privacy", "emo", "video-guides"];
  private readonly WELCOME_PATH = "/welcome";
  private readonly LOGIN_PATH = "/login";
  private readonly NEW_USER_LOGIN_PATH = "login/new-user";
  private readonly SETTINGS_CONTACT_PATH = "/settings/contact-info";
  private readonly PREMIUM_SERVICES_PATH = "/premium-services";
  private readonly IDLE_IDLING_KEY = "ng2Idle.main.idling";
  private readonly IDLE_EXPIRY_KEY = "ng2Idle.main.expiry";

  isNew$ = new BehaviorSubject<boolean>(this.getSessionStorageValue(this.IS_NEW_KEY));
  isLoggingOut$ = new BehaviorSubject<boolean>(null);

  isIdCardAuthenticate: boolean = this.getSessionStorageValue(this.IS_ID_CARD_AUTH_KEY);
  isLoggedOut: boolean = this.getSessionStorageValue(this.IS_LOGGED_OUT_KEY);

  logOutUrl = environment.backend.baseUrl + environment.backend.contextPath + "/logout";

  constructor(
    private http: HttpClient,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private webAuthnService: WebAuthnService,
    private indexedDbService: IndexedDbService,
    private userInfoService: UserInfoService,
  ) {
  }

  private getSessionStorageValue(key: string): boolean | null {
    const val = sessionStorage.getItem(key);
    if (val === null) return null;
    return val === "true";
  }

  private setSessionStorageAndSubject(
    key: string,
    value: boolean | null,
    subject?: BehaviorSubject<boolean>
  ): void {
    if (value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, String(value));
    }
    if (subject) {
      subject.next(value);
    }
  }

  isSessionInitialized$(): Observable<boolean> {
    return this.userInfoService.sessionContext$.pipe(
      map(ctx => !!ctx)
    );
  }

  isAuthenticated$(): Observable<boolean> {
    return this.userInfoService.sessionContext$.pipe(
      map(ctx => !!ctx?.isAuthorizedUser)
    );
  }

  isTermsAccepted$(): Observable<boolean> {
    return this.userInfoService.sessionContext$.pipe(
      map(ctx => !!ctx?.termsOutput?.termsAccepted)
    );
  }

  getIsLoggingOut(): Observable<boolean> {
    return this.isLoggingOut$.asObservable();
  }

  async login(): Promise<void> {
    this.isLoggingOut$.next(false);
    try {
      if (this.sessionStorageService.getRedirectToPremiumAfterLogin()) {
        await this.router.navigate([this.PREMIUM_SERVICES_PATH]).then(() =>
          this.sessionStorageService.removeRedirectToPremiumAfterLogin()
        );
      } else {
        await this.router.navigate(["/"]);
      }
    } catch (error) {
      console.error("Navigation error during login:", error)
      await this.router.navigate(["/"]);
    }
  }

  loginGoToSettings(): void {
    this.removeNew();
    this.router.navigate([this.SETTINGS_CONTACT_PATH]);
  }

  newUserLogin(): void {
    this.isLoggingOut$.next(false);
    this.setIsNew();
    this.router.navigate([this.NEW_USER_LOGIN_PATH]);
  }

  setIsNew(): void {
    this.setSessionStorageAndSubject(this.IS_NEW_KEY, true, this.isNew$);
  }

  setIsNewToNull(): void {
    this.setSessionStorageAndSubject(this.IS_NEW_KEY, null, this.isNew$);
  }

  removeNew(): void {
    this.setSessionStorageAndSubject(this.IS_NEW_KEY, false, this.isNew$);
  }


  private setIsLoggedOut(value: boolean): void {
    this.setSessionStorageAndSubject(this.IS_LOGGED_OUT_KEY, value, null);
    this.isLoggedOut = value;
  }

  clearIsLoggedOut(): void {
    this.setSessionStorageAndSubject(this.IS_LOGGED_OUT_KEY, null, null);
    this.isLoggedOut = null;
  }

  async clearSession(redirectUrl?: string): Promise<void> {
    this.isLoggingOut$.next(true);
    try {
      await firstValueFrom(
        this.http.get<any>(this.logOutUrl,
          { withCredentials: true, observe: "response" })
      );
      await this.logout(redirectUrl);
    } catch (error) {
      await this.logout();
    }
  }

  async logout(redirectUrl?: string): Promise<void> {
    const currentPath = this.router.url;
    const isWelcomeOrLogin = currentPath.includes(this.WELCOME_PATH) || currentPath === this.LOGIN_PATH;
    if (!isWelcomeOrLogin) {
      await this.clearStoredData()
      await this.handleLogoutNavigation(redirectUrl, currentPath)
    }
  }

  private async clearStoredData(): Promise<void> {
    try {
      await this.indexedDbService.clearAll();
    } catch (error) {
      console.error("Error clearing IndexedDB:", error);
    }

    localStorage.removeItem(this.IDLE_EXPIRY_KEY);
    localStorage.removeItem(this.IDLE_IDLING_KEY);
    sessionStorage.clear();

    if (this.webAuthnService.biometricsLoginUUIDExists) {
      this.sessionStorageService.setBiometricAuthenticationCancelled();
    }

    if (this.isIdCardAuthenticate) {
      this.setIsLoggedOut(true);
    }
  }

  private async handleLogoutNavigation(redirectUrl?: string, currentPath?: string): Promise<void> {
    if (redirectUrl) {
      window.open(redirectUrl, "_self");
    } else if (this.GLOBAL_PATHS_TO_CHECK.includes(currentPath)) {
      this.router.navigate([this.WELCOME_PATH]).then(() => window.location.reload());
    } else {
      window.location.reload();
    }
  }

  setIdCardAuthenticate(value: boolean): void {
    this.isIdCardAuthenticate = value;
    sessionStorage.setItem(this.IS_ID_CARD_AUTH_KEY, value ? "true" : "false");
  }

}
